import {useCallback, useState} from 'react';
import {useFormikContext} from 'formik';
import {useTranslation} from 'react-i18next';

import {ALLOCATION_ACCOUNT_TYPE_TO_DV_MAPPING} from '@/constants/engine';
import {useConfig} from '@/contexts/config';
import {AllocationAccountType} from '@/repositories/beneva-api/endpoints/allocations';
import ConfirmDialog from '../confirm-dialog';
import {useConfirmDialogActions} from '../confirm-dialog/use-confirm-dialog-actions';
import FieldWrapper from '../form/field-wrapper';
import FormControl from '../form/form-control';
import Select from '../form/select';
import {AllocationFormSchemaType} from './allocation-form-schema';

type ConfirmState = {visible: false} | {visible: true; value: AllocationAccountType};

export const AllocationAccountTypeField = () => {
  const {t} = useTranslation('common', {keyPrefix: 'components.advisor-predetermined-allocation-form'});
  const {domainValueResolver} = useConfig();
  const {values, touched, errors, handleChange, handleBlur, setFieldValue} =
    useFormikContext<AllocationFormSchemaType>();
  const [confirmationState, setShowConfirmDialog] = useState<ConfirmState>({visible: false});

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      if (values.accounts.length > 0) {
        setShowConfirmDialog({visible: true, value: e.target.value as AllocationAccountType});
      } else {
        handleChange('accountType')(e.target.value);
      }
    },
    [handleChange, setShowConfirmDialog, values.accounts.length],
  );

  const onConfirmChange = useCallback(() => {
    if (confirmationState.visible) {
      handleChange('accountType')(confirmationState.value);
      setFieldValue('accounts', []);
      setShowConfirmDialog({visible: false});
    }
  }, [confirmationState, handleChange, setFieldValue]);

  const onCancelChange = useCallback(() => {
    setShowConfirmDialog({visible: false});
  }, []);

  const confirmDialogActions = useConfirmDialogActions({
    onCancel: onCancelChange,
    onConfirm: onConfirmChange,
  });

  return (
    <>
      <FormControl
        isRequired={true}
        isInError={!!errors.accountType && touched.accountType}
        errors={touched.accountType && errors.accountType ? [t('errors.account-types')] : []}
      >
        <FieldWrapper label={t('label-account-type')} identifier="account-type">
          <Select
            data-testid="allocation-account-type-input"
            name="allocation-account-type"
            value={values.accountType}
            onChange={onChange}
            onBlur={handleBlur('accountType')}
          >
            {Object.entries(ALLOCATION_ACCOUNT_TYPE_TO_DV_MAPPING).map(([id, dvRef]) => (
              <option key={id} value={id}>
                {domainValueResolver({$ref: dvRef})}
              </option>
            ))}
          </Select>
        </FieldWrapper>
      </FormControl>

      <ConfirmDialog
        isVisible={confirmationState.visible}
        actions={confirmDialogActions}
        text={t('confirm-account-type-change')}
      />
    </>
  );
};
